/**
 * Leaf-level hover target resolution for rendered Slov-lex statute HTML.
 *
 * Both statute renderers — `elements/html-viewer/html-viewer.tsx` and
 * `components/ui/html-link-handler.tsx` — highlight the statute as separate,
 * non-nested rectangles: only the most granular sections (odsek, písmeno, bod)
 * are hover targets, while parent wrappers (článok, paragraf) that contain
 * sub-sections are not. Both carried a byte-identical copy of the index
 * builder; it lives here once so the two cannot drift.
 *
 * The two renderers' *handlers* deliberately still differ (only one of them
 * paints badge highlights, and only one exempts actively-highlighted sections
 * from hover-outline removal), so this module covers the shared index and
 * geometry only, not the event handling.
 */

/** Element IDs that are never hover targets. */
const EXCLUDED_ELEMENT_IDS: readonly string[] = [
  'predpis',
  'predpis.oznacenie',
  'predpis.typ',
  'predpis.datum',
  'predpis.nadpis',
  'predpis.text',
  'predpis.text2',
  'slov-lex-style',
  'iri',
];

/** Class names whose elements are never hover targets. */
const EXCLUDED_CLASS_NAMES: readonly string[] = ['odsekOznacenie'];

/**
 * True for ids that can never be hover targets: explicitly excluded ids, part
 * wrappers, and the `.text` / `.oznacenie` sub-nodes of a section.
 */
function isExcludedHoverId(id: string, excludedIdSet: Set<string>): boolean {
  return (
    excludedIdSet.has(id) ||
    id.startsWith('predpis.cast-') ||
    id.endsWith('.text') ||
    id.endsWith('.oznacenie')
  );
}

/** Pass 1: every id-bearing element that is a candidate hover target. */
function collectValidHoverIds(
  allIdElements: Element[],
  excludedElementIds: readonly string[],
  excludedClassNames: readonly string[]
): Set<string> {
  const excludedIdSet = new Set(excludedElementIds);
  const validIds = new Set<string>();
  for (const el of allIdElements) {
    if (
      !isExcludedHoverId(el.id, excludedIdSet) &&
      !excludedClassNames.some((cls) => el.classList.contains(cls))
    ) {
      validIds.add(el.id);
    }
  }
  return validIds;
}

/**
 * Nearest ancestor of `el` (stopping at `container`) whose id is itself a
 * valid hover target, or `null` when there is none.
 */
function findValidAncestorId(
  el: Element,
  validIds: Set<string>,
  container: HTMLElement
): string | null {
  let parent = el.parentElement;
  while (parent && parent !== container) {
    if (parent.id && validIds.has(parent.id)) {
      return parent.id;
    }
    parent = parent.parentElement;
  }
  return null;
}

/** Pass 2: map each valid id to the valid ids nested directly beneath it. */
function buildChildrenMap(
  allIdElements: Element[],
  validIds: Set<string>,
  container: HTMLElement
): Map<string, string[]> {
  const childrenOf = new Map<string, string[]>();
  for (const el of allIdElements) {
    if (!validIds.has(el.id)) continue;
    const parentId = findValidAncestorId(el, validIds, container);
    if (!parentId) continue;
    const siblings = childrenOf.get(parentId);
    if (siblings) {
      siblings.push(el.id);
    } else {
      childrenOf.set(parentId, [el.id]);
    }
  }
  return childrenOf;
}

/**
 * Pass 3: derive the leaf hover targets.
 *  - No children  → leaf (standalone odsek, písmeno, …)
 *  - 1 child      → collapse: the parent is the leaf (§ 1 plus its single
 *                   odsek render as one block)
 *  - 2+ children  → each child is a leaf AND the parent is a leaf, so hovering
 *                   a child (odsek) highlights just that child, while hovering
 *                   the section header (oznacenie) traverses up to the parent
 *                   and highlights the whole section (header + all children).
 */
function computeLeafIds(
  validIds: Set<string>,
  childrenOf: Map<string, string[]>
): Set<string> {
  const computed = new Set<string>();
  for (const id of validIds) {
    const children = childrenOf.get(id);
    if (!children || children.length <= 1) {
      computed.add(id);
    }
  }
  for (const [parentId, children] of childrenOf) {
    if (children.length > 1) {
      for (const childId of children) {
        computed.add(childId);
      }
      computed.add(parentId);
    }
  }
  return computed;
}

/**
 * Builds the leaf-level hover target index for a rendered statute.
 *
 * Walks every id-bearing node in the document, then re-walks each one's
 * ancestor chain to derive the parent→children map — the most expensive thing
 * either renderer does, which is why both defer or memoize the call.
 */
export function buildLeafHoverIds(
  container: HTMLElement,
  excludedElementIds: readonly string[] = EXCLUDED_ELEMENT_IDS,
  excludedClassNames: readonly string[] = EXCLUDED_CLASS_NAMES
): Set<string> {
  const allIdElements = Array.from(container.querySelectorAll('[id]'));
  const validIds = collectValidHoverIds(
    allIdElements,
    excludedElementIds,
    excludedClassNames
  );
  const childrenOf = buildChildrenMap(allIdElements, validIds, container);
  return computeLeafIds(validIds, childrenOf);
}

/**
 * Nearest leaf-level hover target, walking up from `el`. Only `Set.has()`
 * checks — zero DOM queries, zero layout reads. Memoized per element, so
 * repeated hovers on the same node are O(1).
 *
 * `getIds` is a thunk, resolved only on a cache miss, so a repeat hover never
 * forces the index to be built.
 */
export function findLeafHoverTarget(
  el: HTMLElement,
  container: HTMLElement,
  getIds: () => Set<string>,
  cache: WeakMap<HTMLElement, HTMLElement | null>
): HTMLElement | null {
  const cached = cache.get(el);
  if (cached !== undefined) return cached;

  const ids = getIds();
  let node: HTMLElement | null = el;
  while (node && node !== container) {
    if (node.id && ids.has(node.id)) {
      cache.set(el, node);
      return node;
    }
    node = node.parentElement;
  }
  cache.set(el, null);
  return null;
}

/**
 * Menu offset relative to the container. Read BEFORE any class changes, so the
 * write phase that follows cannot force a reflow.
 */
export function readMenuOffset(
  container: HTMLElement,
  target: HTMLElement
): { left: number; top: number } {
  const containerRect = container.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  return {
    left: Math.max(0, rect.left - containerRect.left - 16),
    top: rect.top - containerRect.top,
  };
}

/** True when the user currently has a non-empty text selection. */
export function hasTextSelection(): boolean {
  const selection = window.getSelection();
  return Boolean(selection && selection.toString().length > 0);
}
